import { useCallback } from 'react';
import { logger } from 'modules/logger';
import { ReporterSaveState } from 'config/recoilState';
import type { LoadedReportWorklistItem } from 'hooks/useCurrentCaseReport';
import type { AutoloadTemplateState } from 'hooks/useAutoloadTemplate';
import { ReportStatus } from '../state';
import type { ReportStatuses } from '../state';
import type { SlateContent } from '../types';
import type { Addendum } from './fieldsReducer';
import { useAutoSaveReport } from './useAutoSaveReport';

type UseAutoSaveReportReturn = ReturnType<typeof useAutoSaveReport>;

/**
 * Owns the explicit draft-save workflow. Decides report-draft vs addendum-draft based on status and
 * delegates the actual persistence to useAutoSaveReport (fieldsUpdate / addendumUpdate). Records the
 * just-drafted smid so autoload excludes it for the tab session (WF-1809).
 */
export interface UseDraftReportParams {
  savingState: UseAutoSaveReportReturn['savingState'];
  setSavingState: UseAutoSaveReportReturn['setSavingState'];
  fieldsUpdate: UseAutoSaveReportReturn['fieldsUpdate'];
  addendumUpdate: UseAutoSaveReportReturn['addendumUpdate'];
  autoloadTemplateState: AutoloadTemplateState | undefined;
  worklistItem: LoadedReportWorklistItem;
  status: ReportStatuses;
  content: SlateContent;
  activateTemplateID: string | undefined;
  addendum: Nullable<Addendum>;
  addendumSMID: Nullable<string>;
  isRecording: boolean;
  stopRecording: () => Promise<void>;
  loadDiscardedSmidsFromSessionStorage: () => Set<string>;
  saveDiscardedSmidsInSessionStorage: (discardedSmids: Set<string>) => void;
}

export const useDraftReport = ({
  savingState,
  setSavingState,
  fieldsUpdate,
  addendumUpdate,
  autoloadTemplateState,
  worklistItem,
  status,
  content,
  activateTemplateID,
  addendum,
  addendumSMID,
  isRecording,
  stopRecording,
  loadDiscardedSmidsFromSessionStorage,
  saveDiscardedSmidsInSessionStorage,
}: UseDraftReportParams) => {
  const handleSaveDraft = useCallback(
    async ({
      onSuccess,
      onError,
    }: {
      onSuccess?: () => void;
      onError?: () => void;
    } = {}) => {
      if (savingState === ReporterSaveState.Saving) {
        return;
      }

      if (autoloadTemplateState === 'pending') {
        logger.warn(
          '[useDraftReport] Attempted to save draft while template autoload is still pending',
          {
            worklistItemSmid: worklistItem.smid,
            status,
          }
        );
        onError?.();
        return;
      }

      // WF-1809: Records the just-drafted SMID so autoload excludes it for the
      // rest of the tab session - same bucket as discard. Without this, drafted
      // cases stayed eligible and the FE's items.find() looped between the two
      // earliest-sorted cases.
      const recordDiscardedSmid = () => {
        const discardedSmids = loadDiscardedSmidsFromSessionStorage();
        discardedSmids.add(worklistItem.smid);
        saveDiscardedSmidsInSessionStorage(discardedSmids);
      };

      if (status !== ReportStatus.AddendumAdd) {
        setSavingState(ReporterSaveState.Saving);
        await fieldsUpdate({
          contentToSubmit: content,
          activateTemplateIDToSubmit: activateTemplateID,
          isExplicitDraftSave: true,
          onSuccess: () => {
            recordDiscardedSmid();
            onSuccess?.();
          },
          onError,
        });
      } else if (status === ReportStatus.AddendumAdd && addendumSMID != null) {
        setSavingState(ReporterSaveState.Saving);
        await addendumUpdate({
          contentToSubmit: addendum?.content,
          addendumSMID,
          isExplicitDraftSave: true,
          onSuccess: () => {
            recordDiscardedSmid();
            onSuccess?.();
          },
          onError,
        });
      } else {
        logger.info('[useDraftReport] Draft report did not save.', {
          status,
          addendumSMID,
          worklistItemSmid: worklistItem.smid,
          content: status === ReportStatus.AddendumAdd ? addendum?.content : content,
        });
      }

      if (isRecording) {
        await stopRecording();
      }
    },
    [
      autoloadTemplateState,
      savingState,
      status,
      addendumSMID,
      isRecording,
      setSavingState,
      fieldsUpdate,
      content,
      activateTemplateID,
      addendumUpdate,
      addendum?.content,
      worklistItem.smid,
      stopRecording,
      loadDiscardedSmidsFromSessionStorage,
      saveDiscardedSmidsInSessionStorage,
    ]
  );

  return { handleSaveDraft };
};
